import React, { useState } from "react";
import { KeyRound, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Self-service change-password modal opened from the user menu in AppLayout.
 *  Requires the user's current password + a new password >= 6 characters.
 */
export default function ChangePasswordModal({ onClose }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation don't match");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("New password must be different from current");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated. You're still signed in.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={submit}
        data-testid="change-password-modal"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Change password
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3">
          <Field
            label="Current password"
            testid="change-pw-current"
            value={currentPassword}
            onChange={setCurrentPassword}
            enforceMinLength={false}
          />
          <Field
            label="New password"
            testid="change-pw-new"
            value={newPassword}
            onChange={setNewPassword}
            hint="Minimum 8 characters"
          />
          <Field
            label="Confirm new password"
            testid="change-pw-confirm"
            value={confirmPassword}
            onChange={setConfirmPassword}
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="change-pw-submit"
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 text-white text-sm font-semibold"
          >
            <KeyRound className="w-4 h-4" />
            {busy ? "Updating…" : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid, hint, enforceMinLength = true }) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
        {label}
      </label>
      <input
        required
        minLength={enforceMinLength ? 8 : undefined}
        type="password"
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none font-mono"
      />
      {hint && <div className="mt-1 text-[11px] text-slate-400 font-mono">{hint}</div>}
    </div>
  );
}
